import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, Copy, KeyRound } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../../ui';

// The copy-once moment for a freshly issued / rotated API key.
//
// This is the ONE place the SPA ever sees a raw secret, and the secret is irrecoverable once
// dismissed — so dismissal is deliberately heavy:
//   • NOT closable by Escape or backdrop click (unlike the generic Modal) — only the explicit
//     acknowledge button dismisses it.
//   • The acknowledge button is disabled until the operator ticks "I've stored this key" —
//     making accidental dismissal essentially impossible.
//   • role="alertdialog" + aria-modal + an aria-live region announce it to screen readers.
//   • Focus is trapped within the dialog and moved to it on open (WCAG 2.4.3 / 2.1.2).
//   • The raw value is held in component state only; it is never persisted or logged, and is
//     dropped from memory when the dialog unmounts.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface RawKeyRevealModalProps {
  isOpen: boolean;
  /** The one-time raw secret to reveal. */
  rawKey: string;
  /** Human label for the key being revealed (e.g. the prefix). */
  keyLabel?: string | undefined;
  /** Whether this reveal is from a rotation (changes the copy slightly). */
  rotated?: boolean | undefined;
  /** Called only once the operator acknowledges they have stored the key. */
  onAcknowledge: () => void;
}

export function RawKeyRevealModal({
  isOpen,
  rawKey,
  keyLabel,
  rotated = false,
  onAcknowledge,
}: RawKeyRevealModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Tracks the "Copied" reset timer so it can be cancelled — acknowledging within the 2s window
  // unmounts this component, and a pending timer would otherwise setState on the unmounted node.
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset friction state whenever a new key is revealed, and clear any pending copy timer on
  // close/unmount so it can't fire after the dialog is gone.
  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
      setCopied(false);
    }
    return () => {
      if (copyTimerRef.current !== null) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
    };
  }, [isOpen, rawKey]);

  // Move focus into the dialog on open; restore it on close.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  // Focus trap: keep Tab/Shift+Tab inside the dialog. Escape is intentionally swallowed so the
  // operator cannot dismiss the irrecoverable secret by reflex.
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard?.writeText(rawKey);
      setCopied(true);
      // Reset the "Copied" label after 2s. Track the id so the effect cleanup can cancel it if
      // the operator acknowledges (unmounting this component) before it fires.
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        copyTimerRef.current = null;
        setCopied(false);
      }, 2000);
    } catch {
      // Clipboard may be blocked (insecure context / denied permission). The key stays
      // visible for manual copy; we surface no error to avoid implying the key changed.
      setCopied(false);
    }
  }, [rawKey]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
      data-testid="raw-key-overlay"
    >
      {/* Backdrop — NON-dismissing (no onClick handler). */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="raw-key-title"
        aria-describedby="raw-key-warning"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`relative flex w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-2xl outline-none ${
          isDark
            ? 'border border-amber-500/30 bg-gradient-to-br from-[#1a1a2e] to-[#12121a] shadow-black/50'
            : 'border border-amber-300 bg-white shadow-slate-200/60'
        }`}
        style={{ animation: 'rawKeyIn 0.3s ease-out forwards' }}
      >
        <style>{`@keyframes rawKeyIn { from { opacity: 0; transform: scale(0.96) translateY(10px);} to { opacity: 1; transform: scale(1) translateY(0);} }`}</style>

        {/* Header */}
        <div className="flex items-start gap-4 p-6 pb-4">
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/20 to-orange-600/10 text-amber-400">
            <KeyRound className="h-7 w-7" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2
              id="raw-key-title"
              className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {rotated ? 'New secret — copy it now' : 'Your API key — copy it now'}
            </h2>
            <p className={`mt-0.5 text-sm ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
              {keyLabel ? `${keyLabel} · ` : ''}This is the only time it will ever be shown.
            </p>
          </div>
        </div>

        {/* Unmissable warning banner */}
        <div
          id="raw-key-warning"
          role="status"
          aria-live="assertive"
          className={`mx-6 flex items-center gap-3 rounded-xl p-3.5 ${
            isDark
              ? 'border border-amber-500/30 bg-amber-500/10'
              : 'border border-amber-300 bg-amber-50'
          }`}
        >
          <AlertTriangle
            className="h-5 w-5 flex-shrink-0 text-amber-400"
            aria-hidden="true"
          />
          <p className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
            Store this secret in your password manager now. We never store it and cannot show
            it again — if you lose it you must rotate the key.
          </p>
        </div>

        {/* The secret + copy control */}
        <div className="p-6 pt-4">
          <div
            className={`flex items-center gap-2 rounded-xl border p-2 pl-4 ${
              isDark ? 'border-white/[0.1] bg-black/40' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <code
              data-testid="raw-key-value"
              className={`flex-1 select-all break-all font-mono text-sm ${
                isDark ? 'text-emerald-300' : 'text-emerald-700'
              }`}
            >
              {rawKey}
            </code>
            <Button
              variant={copied ? 'success' : 'secondary'}
              size="sm"
              onClick={() => void handleCopy()}
              leftIcon={
                copied ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Copy className="h-4 w-4" aria-hidden="true" />
                )
              }
              aria-label={copied ? 'Key copied to clipboard' : 'Copy key to clipboard'}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>

          {/* Acknowledgement gate */}
          <label
            className={`mt-5 flex cursor-pointer items-start gap-3 text-sm ${
              isDark ? 'text-white/70' : 'text-slate-600'
            }`}
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-white/20 text-indigo-500 focus:ring-2 focus:ring-indigo-500/40"
            />
            <span>
              I have copied and securely stored this key. I understand it cannot be shown
              again.
            </span>
          </label>
        </div>

        {/* Footer — single acknowledge action, gated on the checkbox. */}
        <div className="p-6 pt-0">
          <Button
            variant="primary"
            fullWidth
            disabled={!acknowledged}
            onClick={onAcknowledge}
          >
            Done — I&apos;ve stored my key
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
